import { useEffect, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import ScrollToTopButton from '../components/ScrollToTopButton';
import {
  CONSOLE_INFO,
  clearUserSession,
  getUserSession,
  type ConsoleKey,
  type UserSession,
} from '../screens/login/userSession';
import './Manage.scss';

// 교회앱관리(/manage-app) · 교적관리(/manage-roll) · 재정관리(/manage-finance)가 공유하는 틀.
// 계정(churchAdmin.admins)은 같지만 로그인·세션은 콘솔마다 따로라, 상단바에서 다른 콘솔로 넘어가지 않는다.
// 주일학교관리(/manage-ss)는 계정 자체가 별도라 여기에 포함하지 않는다.

export type ManageMenuItem<K extends string> = { key: K; label: string; icon: string };
export type ManageMenuGroup<K extends string> = { group: string; items: ManageMenuItem<K>[] };

type Props<K extends string> = {
  console: ConsoleKey;
  menuGroups: ManageMenuGroup<K>[];
  activeMenu: K;
  onSelectMenu: (key: K) => void;
  children: (session: UserSession) => ReactNode;
};

export default function ManageShell<K extends string>({
  console: activeConsole,
  menuGroups,
  activeMenu,
  onSelectMenu,
  children,
}: Props<K>) {
  const navigate = useNavigate();
  const session = getUserSession(activeConsole);
  const sidebarRef = useRef<HTMLElement>(null);

  // 좁은 화면에선 메뉴가 가로 스크롤 탭 줄이라, 선택된 메뉴가 보이도록 그 줄만 옮긴다.
  useEffect(() => {
    const bar = sidebarRef.current;
    const active = bar?.querySelector<HTMLElement>('.manage-menu-btn.is-active');
    if (!bar || !active || bar.scrollWidth <= bar.clientWidth) return;
    const offset = active.getBoundingClientRect().left - bar.getBoundingClientRect().left;
    bar.scrollLeft += offset - (bar.clientWidth - active.offsetWidth) / 2;
  }, [activeMenu, menuGroups]);

  useEffect(() => {
    if (!session) {
      navigate(`/login?console=${activeConsole}`, { replace: true });
    }
  }, [session, navigate, activeConsole]);

  if (!session) return null;

  const logout = () => {
    clearUserSession(activeConsole);
    navigate(`/login?console=${activeConsole}`, { replace: true });
  };

  return (
    <div className="manage-page">
      <header className="manage-topbar">
        <div className="manage-topbar-left">
          <span className="manage-topbar-brand">교회앱모아<span>ADMIN</span></span>
          <span className="manage-console-title">{CONSOLE_INFO[activeConsole].label}</span>
        </div>
        <div className="manage-topbar-actions">
          <button type="button" className="manage-btn manage-btn--sm" onClick={() => navigate('/')}>홈</button>
          <button type="button" className="manage-btn manage-btn--sm" onClick={logout}>로그아웃</button>
        </div>
      </header>

      <section className="manage-shell-section">
        <div className="container manage-shell">
          <aside className="manage-sidebar" ref={sidebarRef}>
            <nav className="manage-menu">
              {menuGroups.map((group) => (
                <div key={group.group}>
                  <div className="manage-menu-group">{group.group}</div>
                  {group.items.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className={`manage-menu-btn${activeMenu === item.key ? ' is-active' : ''}`}
                      onClick={() => {
                        window.scrollTo(0, 0);
                        onSelectMenu(item.key);
                      }}
                    >
                      <span className="manage-menu-icon" aria-hidden>{item.icon}</span>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              ))}
            </nav>
          </aside>

          <div className="manage-content">{children(session)}</div>
        </div>
      </section>

      <ScrollToTopButton />
    </div>
  );
}
